/**
 * O compositor do modal do Negócio (11.4): abas **Nota** + um tipo de tarefa por aba.
 *
 * - **Nota** é texto livre (`POST /negocios/{id}/notas`). Nota registrada **não** é contato: a
 *   régua só zera por tarefa concluída ou mensagem enviada (AD-10) — o compositor diz isso ao lado
 *   do botão, para "nota registrada" não ser lida como "contato feito".
 * - **Tarefa** nasce com o tipo (a aba), **Título** próprio, descrição opcional, prazo com **hora**
 *   (sem hora é o dia inteiro — 23:59:59 em São Paulo), responsável (qualquer usuário) e um
 *   **anexo** opcional (11.6, FR-80): o anexo sobe logo depois da criação, pela rota da Tarefa.
 *
 * O rascunho é deste componente; ele avisa quem o monta quando há algo digitado (`aoMudarRascunho`)
 * — é assim que o modal pergunta antes de fechar com alteração pendente. O aceite da sugestão de
 * próximo passo (concluir tarefa no histórico) chega por `preencherComSugestao` (ref imperativa):
 * a aba do tipo abre com o prazo preenchido e o foco no título.
 */

import {
  type FormEvent,
  type KeyboardEvent,
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { IconeDoTipo } from './IconeDoTipo'
import {
  TAMANHO_DO_TEXTO_DA_NOTA,
  useAnexarArquivo,
  useCriarNota,
  useCriarTarefa,
  useTiposDeTarefa,
} from './api'
import { TAMANHO_DA_DESCRICAO, TAMANHO_DO_TITULO, instanteDoPrazo } from './apresentacao'
import {
  type EstadoDoCatalogo,
  ACEITA_NO_ANEXO,
  CARREGANDO_OS_TIPOS,
  EQUIPE_INDISPONIVEL,
  NAO_HA_TIPO,
  TIPOS_INDISPONIVEIS,
  recusaDoAnexo,
  recusaLocal,
  rotuloDoResponsavel,
  tipoDerivado,
} from './regras-de-tarefa'
import './compositor-do-negocio.css'

const ABA_DA_NOTA = 'nota'

export const NOTA_REGISTRADA = 'Nota registrada.'
export const TAREFA_CRIADA_NO_COMPOSITOR = 'Tarefa criada.'
export const FALTA_TEXTO_DA_NOTA = 'Escreva a nota antes de salvar.'
/** A tarefa nasceu, e o anexo não subiu: a frase do serviço vem depois (11.6). */
export const ANEXO_NAO_SUBIU = 'Tarefa criada, mas o anexo não foi enviado:'
/** O aviso fixo da aba Nota: nota registrada não é contato (AD-10). */
export const NOTA_NAO_E_CONTATO =
  'Nota não conta como contato: para zerar os dias sem contato, conclua uma tarefa ou envie mensagem.'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O que o modal pode pedir ao compositor por fora. */
export interface ControleDoCompositor {
  preencherComSugestao: (tipoDeTarefaId: string, prazo: string) => void
  /** Devolve o foco ao campo da aba aberta (a sugestão foi recusada e o gatilho sumiu). */
  focar: () => void
}

export interface PropsDoCompositor {
  negocioId: string
  /** Avisa quando passa a haver (ou deixa de haver) algo digitado e não salvo. */
  aoMudarRascunho?: (pendente: boolean) => void
  controle?: Ref<ControleDoCompositor>
}

export function CompositorDoNegocio({ negocioId, aoMudarRascunho, controle }: PropsDoCompositor) {
  const { data: sessao } = useSessao()
  const catalogo = useTiposDeTarefa()
  const equipe = useResponsaveis()
  const criarNota = useCriarNota(negocioId)
  const criarTarefa = useCriarTarefa()
  //: O upload pela mutação: ela relê as Tarefas (o histórico passa a mostrar o anexo).
  const upload = useAnexarArquivo()

  const [aba, setAba] = useState<string>(ABA_DA_NOTA)
  const [texto, setTexto] = useState('')
  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [prazo, setPrazo] = useState('')
  const [hora, setHora] = useState('')
  const [anexo, setAnexo] = useState<File | null>(null)
  const campoDoAnexo = useRef<HTMLInputElement>(null)
  const [responsavel, setResponsavel] = useState('')
  const [recusa, setRecusa] = useState<string | null>(null)
  const [feito, setFeito] = useState<string | null>(null)
  const campoDoTitulo = useRef<HTMLInputElement>(null)

  const tipos = catalogo.data ?? []
  const daEquipe = equipe.data ?? []
  const escolhiveis = daEquipe.filter((pessoa) => pessoa.ativo)
  const tipoDaAba = tipos.find((tipo) => tipo.id === aba)
  const responsavelEscolhido = responsavel !== '' ? responsavel : (sessao?.usuario_id ?? '')
  const responsavelSemRotulo =
    responsavelEscolhido !== '' && !escolhiveis.some((pessoa) => pessoa.id === responsavelEscolhido)

  const pendente =
    texto.trim() !== '' ||
    titulo.trim() !== '' ||
    descricao.trim() !== '' ||
    prazo !== '' ||
    hora !== '' ||
    anexo !== null
  useEffect(() => {
    aoMudarRascunho?.(pendente)
  }, [pendente, aoMudarRascunho])

  const campoDaNota = useRef<HTMLTextAreaElement>(null)
  const focarNoCampo = () => {
    window.setTimeout(() => {
      ;(campoDoTitulo.current ?? campoDaNota.current)?.focus()
    }, 0)
  }

  useImperativeHandle(controle, () => ({
    preencherComSugestao: (tipoDeTarefaId: string, prazoDaSugestao: string) => {
      /* O tipo excluído do catálogo cai no primeiro ativo (o antigo `tipoDerivado`). Sem tipo
         nenhum, a aba é a Nota e o prazo **não** é preenchido: seria um campo invisível. */
      const destino = tipoDerivado(tipoDeTarefaId, tipos)
      setAba(destino || ABA_DA_NOTA)
      setPrazo(destino === '' ? '' : prazoDaSugestao)
      setRecusa(null)
      setFeito(null)
      focarNoCampo()
    },
    focar: focarNoCampo,
  }))

  const limparAvisos = () => {
    setRecusa(null)
    setFeito(null)
    if (!criarNota.isPending) {
      criarNota.reset()
    }
    if (!criarTarefa.isPending) {
      criarTarefa.reset()
    }
  }

  const trocarDeAba = (destino: string) => {
    setAba(destino)
    limparAvisos()
  }

  /* As abas no padrão ARIA: só a selecionada entra no Tab; setas, Home e End trocam e focam. */
  const idsDasAbas = [ABA_DA_NOTA, ...tipos.map((tipo) => tipo.id)]
  const abaSelecionada = tipoDaAba === undefined ? ABA_DA_NOTA : aba
  const aoTeclarNasAbas = (evento: KeyboardEvent<HTMLDivElement>) => {
    const atual = idsDasAbas.indexOf(abaSelecionada)
    const ultima = idsDasAbas.length - 1
    const destinos: Record<string, number> = {
      ArrowRight: atual === ultima ? 0 : atual + 1,
      ArrowLeft: atual === 0 ? ultima : atual - 1,
      Home: 0,
      End: ultima,
    }
    const posicao = destinos[evento.key]
    if (posicao === undefined) {
      return
    }
    evento.preventDefault()
    const destino = idsDasAbas[posicao] ?? ABA_DA_NOTA
    trocarDeAba(destino)
    document.getElementById(`compositor-aba-${destino}`)?.focus()
  }

  const salvarNota = (evento: FormEvent) => {
    evento.preventDefault()
    limparAvisos()
    const limpo = texto.trim()
    if (limpo === '') {
      setRecusa(FALTA_TEXTO_DA_NOTA)
      return
    }
    criarNota.mutate(limpo, {
      onSuccess: () => {
        /* Só esvazia se o campo ainda é o que foi enviado: o que se digitou durante o envio fica. */
        setTexto((atual) => (atual.trim() === limpo ? '' : atual))
        setFeito(NOTA_REGISTRADA)
      },
    })
  }

  const estadoDoCatalogo: EstadoDoCatalogo = catalogo.isError
    ? 'indisponivel'
    : catalogo.isLoading
      ? 'carregando'
      : 'pronto'

  const salvarTarefa = (evento: FormEvent) => {
    evento.preventDefault()
    limparAvisos()
    //: O anexo é conferido **antes** de criar: recusado depois, a Tarefa já existiria sem ele.
    const local =
      recusaLocal({
        titulo,
        prazo,
        tipoEscolhido: tipoDaAba?.id ?? '',
        responsavelEscolhido,
        catalogo: estadoDoCatalogo,
      }) ?? recusaDoAnexo(anexo)
    if (local !== null) {
      setRecusa(local)
      return
    }
    const enviado = titulo.trim()
    const prazoEnviado = prazo
    const anexoEnviado = anexo
    const descricaoEnviada = descricao.trim()
    criarTarefa.mutate(
      {
        titulo: enviado,
        descricao: descricaoEnviada === '' ? null : descricaoEnviada,
        prazo: instanteDoPrazo(prazo, hora),
        tipo_de_tarefa_id: tipoDaAba?.id ?? '',
        responsavel_id: responsavelEscolhido,
        negocio_id: negocioId,
      },
      {
        onSuccess: (criada) => {
          void aposCriar(criada)
        },
      },
    )

    async function aposCriar(criada: { id: string }) {
      /* Mesma regra da nota: só esvazia o que ainda é o que foi enviado. */
      setTitulo((atual) => (atual.trim() === enviado ? '' : atual))
      setDescricao((atual) => (atual.trim() === descricaoEnviada ? '' : atual))
      setPrazo((atual) => (atual === prazoEnviado ? '' : atual))
      setHora('')
      setAnexo(null)
      if (campoDoAnexo.current !== null) {
        campoDoAnexo.current.value = ''
      }
      if (anexoEnviado === null) {
        setFeito(TAREFA_CRIADA_NO_COMPOSITOR)
        return
      }
      /* O anexo sobe **depois** da criação, pela rota da Tarefa (AD-9). A Tarefa já existe:
         a falha do upload não a desfaz, e a frase diz as duas coisas. */
      try {
        await upload.mutateAsync({ tarefaId: criada.id, arquivo: anexoEnviado })
        setFeito(TAREFA_CRIADA_NO_COMPOSITOR)
      } catch (falha) {
        setRecusa(`${ANEXO_NAO_SUBIU} ${mensagemDe(falha)}`)
      }
    }
  }

  const erroDoServico = aba === ABA_DA_NOTA ? criarNota.error : criarTarefa.error
  const ocupado = criarNota.isPending || criarTarefa.isPending

  const avisos = (
    <>
      {recusa !== null ? (
        <p className="ficha__erro" role="alert">
          {recusa}
        </p>
      ) : erroDoServico ? (
        <p className="ficha__erro" role="alert">
          {mensagemDe(erroDoServico)}
        </p>
      ) : feito !== null ? (
        <p className="ficha__salvo" role="status">
          {feito}
        </p>
      ) : null}
    </>
  )

  return (
    <section className="ficha__card compositor" aria-label="Compositor">
      <div
        className="compositor__abas"
        role="tablist"
        aria-label="Registrar no negócio"
        onKeyDown={aoTeclarNasAbas}
      >
        <button
          type="button"
          role="tab"
          id="compositor-aba-nota"
          aria-selected={abaSelecionada === ABA_DA_NOTA}
          tabIndex={abaSelecionada === ABA_DA_NOTA ? 0 : -1}
          aria-controls="compositor-painel"
          className="compositor__aba"
          onClick={() => {
            trocarDeAba(ABA_DA_NOTA)
          }}
        >
          <span className="compositor__icone" aria-hidden="true">
            📝
          </span>
          Nota
        </button>
        {tipos.map((tipo) => (
          <button
            key={tipo.id}
            type="button"
            role="tab"
            id={`compositor-aba-${tipo.id}`}
            aria-selected={abaSelecionada === tipo.id}
            tabIndex={abaSelecionada === tipo.id ? 0 : -1}
            aria-controls="compositor-painel"
            className="compositor__aba"
            onClick={() => {
              trocarDeAba(tipo.id)
            }}
          >
            {tipo.icone === null || tipo.icone === '' ? null : (
              <span className="compositor__icone" aria-hidden="true">
                <IconeDoTipo icone={tipo.icone} />
              </span>
            )}
            {tipo.nome}
          </button>
        ))}
      </div>

      <div
        className="ficha__card-corpo compositor__painel"
        role="tabpanel"
        id="compositor-painel"
        aria-labelledby={
          aba === ABA_DA_NOTA || tipoDaAba === undefined
            ? 'compositor-aba-nota'
            : `compositor-aba-${aba}`
        }
      >
        {aba === ABA_DA_NOTA || tipoDaAba === undefined ? (
          <form className="compositor__formulario" onSubmit={salvarNota}>
            {/* O rótulo só para o leitor de tela (protótipo v2: a área de texto fala por si, com
                o texto-guia); a frase sobre contato vai ao rodapé, ao lado do botão. */}
            <label className="ficha__so-para-leitor" htmlFor="compositor-nota">
              Nota
            </label>
            <textarea
              id="compositor-nota"
              ref={campoDaNota}
              className="ficha__entrada ficha__entrada--longa compositor__nota"
              value={texto}
              maxLength={TAMANHO_DO_TEXTO_DA_NOTA}
              placeholder="O que foi feito e qual o próximo passo?"
              onChange={(evento) => {
                limparAvisos()
                setTexto(evento.target.value)
              }}
            />
            <div className="compositor__rodape">
              <p className="ficha__ajuda compositor__dica">{NOTA_NAO_E_CONTATO}</p>
              <button
                type="submit"
                className="ficha__botao ficha__botao--primario"
                disabled={ocupado}
              >
                {criarNota.isPending ? 'Salvando…' : 'Salvar nota'}
              </button>
            </div>
            {avisos}
          </form>
        ) : (
          <form className="compositor__formulario" onSubmit={salvarTarefa}>
            <div className="tarefas__campo">
              <label className="ficha__linha-rotulo" htmlFor="compositor-titulo">
                Título
              </label>
              <input
                id="compositor-titulo"
                ref={campoDoTitulo}
                className="ficha__entrada"
                type="text"
                value={titulo}
                maxLength={TAMANHO_DO_TITULO}
                onChange={(evento) => {
                  limparAvisos()
                  setTitulo(evento.target.value)
                }}
              />
            </div>
            <div className="tarefas__campo">
              <label className="ficha__linha-rotulo" htmlFor="compositor-descricao">
                Descrição (opcional)
              </label>
              <textarea
                id="compositor-descricao"
                className="ficha__entrada"
                value={descricao}
                maxLength={TAMANHO_DA_DESCRICAO}
                onChange={(evento) => {
                  limparAvisos()
                  setDescricao(evento.target.value)
                }}
              />
            </div>
            <div className="compositor__linha">
              <div className="tarefas__campo">
                <label className="ficha__linha-rotulo" htmlFor="compositor-prazo">
                  Prazo
                </label>
                <input
                  id="compositor-prazo"
                  className="ficha__entrada"
                  type="date"
                  value={prazo}
                  onChange={(evento) => {
                    limparAvisos()
                    setPrazo(evento.target.value)
                  }}
                />
              </div>
              <div className="tarefas__campo">
                <label className="ficha__linha-rotulo" htmlFor="compositor-hora">
                  Hora (opcional)
                </label>
                <input
                  id="compositor-hora"
                  className="ficha__entrada"
                  type="time"
                  value={hora}
                  onChange={(evento) => {
                    limparAvisos()
                    setHora(evento.target.value)
                  }}
                />
              </div>
              <div className="tarefas__campo">
                <label className="ficha__linha-rotulo" htmlFor="compositor-responsavel">
                  Responsável
                </label>
                <select
                  id="compositor-responsavel"
                  className="ficha__selecao"
                  value={responsavelEscolhido}
                  onChange={(evento) => {
                    limparAvisos()
                    setResponsavel(evento.target.value)
                  }}
                >
                  {responsavelSemRotulo ? (
                    <option value={responsavelEscolhido}>
                      {rotuloDoResponsavel(responsavelEscolhido, { sessao, daEquipe })}
                    </option>
                  ) : null}
                  {escolhiveis.map((pessoa) => (
                    <option key={pessoa.id} value={pessoa.id}>
                      {pessoa.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="tarefas__campo">
              <label className="ficha__linha-rotulo" htmlFor="compositor-anexo">
                Anexo (opcional)
              </label>
              <input
                id="compositor-anexo"
                ref={campoDoAnexo}
                className="ficha__entrada"
                type="file"
                accept={ACEITA_NO_ANEXO}
                onChange={(evento) => {
                  limparAvisos()
                  setAnexo(evento.target.files?.[0] ?? null)
                }}
              />
            </div>
            <div className="compositor__rodape">
              <button
                type="submit"
                className="ficha__botao ficha__botao--primario"
                disabled={ocupado}
              >
                {criarTarefa.isPending ? 'Criando…' : `Criar ${tipoDaAba.nome.toLowerCase()}`}
              </button>
            </div>
            {avisos}
            {equipe.isError ? (
              <p className="ficha__erro" role="alert">
                {EQUIPE_INDISPONIVEL}
              </p>
            ) : null}
          </form>
        )}
        {catalogo.isError ? (
          <p className="ficha__erro" role="alert">
            {TIPOS_INDISPONIVEIS}
          </p>
        ) : catalogo.isLoading ? (
          <p className="ficha__ajuda" role="status">
            {CARREGANDO_OS_TIPOS}
          </p>
        ) : tipos.length === 0 ? (
          <p className="ficha__ajuda">{NAO_HA_TIPO}</p>
        ) : null}
      </div>
    </section>
  )
}
